import React, { useState } from 'react'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ClearIcon from '@mui/icons-material/Clear'
import FilterListIcon from '@mui/icons-material/FilterList'
import { SIDEBAR_WIDTH } from '../../constants/layout'

export interface FilterSidebarProps {
  /**
   * The filter controls. A product dashboard passes two
   * (`LabelFilterBuilder` + `MetadataFilterSection`); the admin page passes
   * those plus its own. That difference is COMPOSITION — see the note below on
   * why it must never become a prop.
   */
  children: React.ReactNode
  /**
   * How many filters are currently applied. The clear button appears only above
   * zero, so an untouched sidebar offers no control that would do nothing.
   */
  activeCount: number
  onClearAll: () => void
  /** Disables the clear button while the page is refetching. */
  disabled?: boolean
}

/**
 * The collapsible filter sidebar shared by the social dashboards (T2.1).
 *
 * WHAT THIS IS. Chrome, and only chrome: the shell, the "Filters" header, the
 * collapse control, the scroll region and the clear-all footer. Every control
 * inside it arrives as `children`. It was lifted verbatim from
 * `SocialAnalyticsDashboard.tsx:457-565`, where it was the one thing standing
 * between the five product dashboards and the filter sidebar they were supposed
 * to have.
 *
 * ⚠ WHY THERE IS NO `variant` PROP, AND MUST NEVER BE ONE. The admin dashboard
 * shows three controls the departments do not. The tempting shape is
 * `variant="admin"`, and it is the exact failure `StandardProductDashboard`'s
 * doc comment warns about: five departments' opinions entangled in one file, so
 * that the cheap fork this structure exists to protect stops being cheap. A
 * different page passing different children costs nothing and cannot entangle.
 *
 * ⚠ OVERLAP WITH `components/system/SidebarPage.tsx`, KNOWINGLY ACCEPTED.
 * `SideBarPage` is roughly this shell already — same width, same uppercase
 * header, same collapse-to-a-rail idea. It is NOT reused here for two reasons:
 * it has no footer slot (the clear-all button), and its body is
 * `overflow: hidden` where a filter list needs `overflow: auto`. Adding both to
 * `SideBarPage` would reshape chrome that `Tables.tsx`, `EditSpec.tsx` and
 * `Variables.tsx` depend on, which is a blast radius this ticket cannot verify.
 * Folding the two together later is a small, obvious change; silently
 * reshaping three unrelated pages is not — so the duplication is the reversible
 * side of that trade, and this note is here so it stays a decision rather than
 * an accident.
 *
 * The open/closed state is INTERNAL. Nothing outside reads it — it was local
 * `useState` in the dashboard and stays local here — so composing a sidebar is
 * passing children and nothing else, rather than five departments each carrying
 * the same `open`/`onToggle` boilerplate.
 */
export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  children,
  activeCount,
  onClearAll,
  disabled = false,
}) => {
  const [open, setOpen] = useState(true)

  if (!open) {
    return (
      <Box
        onClick={() => setOpen(true)}
        sx={{
          width: 48,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1.5,
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          cursor: 'pointer',
        }}
      >
        <Tooltip title="Open" placement="right">
          <IconButton
            size="small"
            aria-label="Open filters"
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }

  return (
    <Box
      data-testid="sky-filter-sidebar"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Sidebar header — matches Tables/EditSpec pattern */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 2.5,
          pb: 1.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
          }}
        >
          Filters
        </Typography>
        <Tooltip title="Collapse sidebar">
          <IconButton
            size="small"
            aria-label="Collapse sidebar"
            onClick={() => setOpen(false)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>{children}</Box>

      {activeCount > 0 && (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="text"
            startIcon={<ClearIcon />}
            onClick={onClearAll}
            disabled={disabled}
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
            }}
          >
            Clear All Filters ({activeCount})
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default FilterSidebar
